/* eslint-disable max-len */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ServiceFeeFormFields from "../../components/service-fees/ServiceFeeFormFields";
import FormWrapper from "../../components/ui/FormWrapper";
import { ICity } from "../../interfaces/endpoints/ILocations";
import { OperatorsSelectList } from "../../interfaces/endpoints/IOperators";
import { IServiceFeeFormValues } from "../../interfaces/endpoints/IServiceFee";
import { getAllCities } from "../../services/Locations.service";
import { getAllSelectOperators } from "../../services/Operators.service";
import { createServiceFee, updateServiceFee } from "../../services/ServiceFee.service";
import ServiceFeeSchema from "../../utils/validations/ServiceFee.schema";

const AddServiceFee: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const { state }: any = useLocation();
  const navigate = useNavigate();

  const methods = useForm<IServiceFeeFormValues>({
    resolver: yupResolver(ServiceFeeSchema) as any,
    defaultValues: {
      departureCity: null, arrivalCity: null, operator: null,
      percentage: "", applyBothDirections: false
    }
  });

  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    if (editMode && state?.feeData) {
      const d = state.feeData;
      void Promise.all([
        getAllCities({ pageSize: 3000 }),
        getAllSelectOperators({ pageIndex: 0, pageSize: 2000 })
      ]).then(([citiesRes, operatorsRes]) => {
        const cities: ICity[] = citiesRes.data?.result || [];
        const operators: OperatorsSelectList[] = operatorsRes.data?.result || [];

        const depCity = cities.find((c) => c.id === d.departureCityId);
        const arrCity = cities.find((c) => c.id === d.arrivalCityId);

        if (depCity) setValue("departureCity", depCity);
        if (arrCity) setValue("arrivalCity", arrCity);

        if (d.operatorId) {
          const op = operators.find((o) => o.id === d.operatorId);
          if (op) setValue("operator", op);
        }

        setValue("percentage", (d.percentage * 100).toString());
      });
    }
  }, [editMode, state, setValue]);

  const onSubmit: SubmitHandler<IServiceFeeFormValues> = async (data) => {
    const percentage = parseFloat(data.percentage) / 100;
    const operatorId = data.operator && data.operator.id !== 0 ? data.operator.id : null;

    if (editMode && id) {
      await updateServiceFee({
        id: Number(id), departureCityId: data.departureCity!.id,
        arrivalCityId: data.arrivalCity!.id, operatorId, percentage
      })
        .then(() => {
          toast.success("Konfigurimi është ndryshuar me sukses.", { position: "top-right" });
          setTimeout(() => navigate("/service-fees", { state: ["Service Fees"] }), 500);
        })
        .catch(() => toast.error("Ndodhi një gabim gjatë ndryshimit."));
    } else {
      const base = { departureCityId: data.departureCity!.id, arrivalCityId: data.arrivalCity!.id, operatorId, percentage };
      const requests = [createServiceFee(base)];
      if (data.applyBothDirections) {
        requests.push(createServiceFee({ departureCityId: base.arrivalCityId, arrivalCityId: base.departureCityId, operatorId, percentage }));
      }
      await Promise.all(requests)
        .then(() => {
          toast.success("Konfigurimi është krijuar me sukses.", { position: "top-right" });
          setTimeout(() => navigate("/service-fees", { state: ["Service Fees"] }), 500);
        })
        .catch(() => toast.error("Ndodhi një gabim. Mund të ekzistojë një konfigurim i njëjtë.", { position: "top-right" }));
    }
  };

  return (
    <FormProvider {...methods}>
      <Link className="flex items-center mb-4 text-sm font-semibold text-primary" to="/service-fees" state={["Service Fees"]}>
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        history={{ entityName: "ServiceFeeConfiguration", entityId: id }}
        showPrompt={!methods.formState.isSubmitSuccessful && !!Object.keys(methods.formState.dirtyFields).length}
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko konfigurimin e tarifës" : "Shto konfigurim të ri të tarifës"}
      >
        <ServiceFeeFormFields editMode={editMode} />
      </FormWrapper>
    </FormProvider>
  );
};

export default AddServiceFee;
